import { useState } from 'react';

import { usePermissions } from '../../../contexts/PermissionContext';
import type { DndReason } from '../../../pages/housekeeping/types/housekeeping';
import { useDeclarerNePasDeranger } from '../../api/useActionsMenage';
import BottomSheet from '../../components/BottomSheet';
import Button from '../../components/Button';
import Pills from '../../components/Pills';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';

import ChampTexte from './ChampTexte';

/**
 * Poser « ne pas déranger » — `DndModal` du web, SANS la photo (arbitrage du plan).
 * La source se déduit du droit, comme au web : qui affecte déclare en gouvernante.
 */
const MOTIFS: DndReason[] = ['panneau_porte', 'demande_orale', 'autre'];

/** Libellés lus au rendu : l'app remonte au changement de langue. */
function motifs(): { valeur: DndReason; libelle: string }[] {
    return MOTIFS.map((valeur) => ({ valeur, libelle: t(`menage.ndd.motifs.${valeur}`) }));
}

interface FeuilleNePasDerangerProps {
    serviceId: number;
    onFermer: () => void;
    onPose: () => void;
}

export default function FeuilleNePasDeranger({ serviceId, onFermer, onPose }: FeuilleNePasDerangerProps) {
    const { hasPermission } = usePermissions();
    const declarer = useDeclarerNePasDeranger();
    const [motif, setMotif] = useState<DndReason>('panneau_porte');
    const [note, setNote] = useState('');
    const [echec, setEchec] = useState(false);

    const confirmer = () => {
        setEchec(false);
        declarer.mutate(
            {
                serviceId,
                source: hasPermission('housekeeping.assign') ? 'manager' : 'housekeeper',
                reason: motif,
                ...(note.trim() ? { note: note.trim() } : {}),
            },
            { onSuccess: onPose, onError: () => setEchec(true) },
        );
    };

    return (
        <BottomSheet ouverte onClose={onFermer} libelle={t('menage.ndd.titre')} libelleFermeture={t('commun.fermer')}>
            <div className="flex flex-col gap-4">
                <Text variante="titre" as="h2">
                    {t('menage.ndd.nePasFaire')}
                </Text>
                <Pills libelle={t('menage.ndd.motif')} options={motifs()} valeur={motif} onChange={setMotif} />
                <ChampTexte libelle={t('menage.ndd.note')} valeur={note} onChange={setNote} />
                {echec && (
                    <div role="alert">
                        <Text variante="petit" ton="danger">
                            {t('menage.ndd.echec')}
                        </Text>
                    </div>
                )}
                <Button libelle={t('menage.ndd.confirmer')} pleineLargeur disabled={declarer.isLoading} onPress={confirmer} />
            </div>
        </BottomSheet>
    );
}
